import { useMemo, useState } from "react";
import { Link, useLocation, useRoute } from "wouter";
import {
  ChevronLeft,
  Layers,
  Loader2,
  Play,
  Shuffle,
  Trophy,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCurrentOrg,
  useEvent,
  useMe,
  useSeedTournament,
  useSetMatchBoard,
  useTournament,
} from "@/lib/queries";
import { api, ApiError } from "@/lib/api";
import type { BracketMatch, Game } from "@/lib/types";

export default function TournamentBracket() {
  const [, params] = useRoute("/dashboard/events/:eventSlug/t/:tournamentSlug");
  const [, setLocation] = useLocation();
  const { data: org } = useCurrentOrg();
  const eventSlug = params?.eventSlug ?? null;
  const tournamentSlug = params?.tournamentSlug ?? null;
  const { data: me } = useMe();
  const { data: event } = useEvent(org?.slug ?? null, eventSlug);
  const { data: tournament, isLoading } = useTournament(
    org?.slug ?? null,
    eventSlug,
    tournamentSlug,
  );
  const seed = useSeedTournament(tournament?.id ?? "");
  const setBoard = useSetMatchBoard();
  const [startingMatchId, setStartingMatchId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = tournament?.matches ?? [];
  const byRound = useMemo(() => {
    const m = new Map<number, BracketMatch[]>();
    for (const match of matches) {
      const list = m.get(match.round) ?? [];
      list.push(match);
      m.set(match.round, list);
    }
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [matches]);

  const playerNames = useMemo(() => {
    const out = new Map<string, string>();
    for (const r of event?.registrations ?? []) {
      if (r.player) out.set(r.player.id, r.player.name);
      if (r.team) out.set(r.team.id, r.team.name);
    }
    return out;
  }, [event]);

  const myRole =
    me?.orgs.find((o) => o.orgSlug === org?.slug)?.role ?? null;
  const canEdit =
    me?.isPlatformAdmin || myRole === "owner" || myRole === "admin";

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (!tournament) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <Trophy className="mx-auto text-muted-foreground" size={32} />
        <h1 className="text-2xl font-display uppercase">Not found</h1>
        <Button asChild>
          <Link href="/dashboard/events">Back to events</Link>
        </Button>
      </div>
    );
  }

  async function startMatch(match: BracketMatch) {
    if (!event || !tournament) return;
    setError(null);
    setStartingMatchId(match.id);
    try {
      const team1PlayerIds = match.slot1.playerId
        ? [match.slot1.playerId]
        : null;
      const team2PlayerIds = match.slot2.playerId
        ? [match.slot2.playerId]
        : null;
      if (!team1PlayerIds || !team2PlayerIds) {
        throw new Error("Match not ready — slots unresolved.");
      }
      const created = await api<Game>("/games", {
        method: "POST",
        body: {
          playedOn: new Date().toISOString().slice(0, 10),
          format: "singles",
          team1PlayerIds,
          team2PlayerIds,
          targetScore: tournament.rules?.targetScore ?? 21,
          bustRule: tournament.rules?.bustRule ?? "none",
          skunkRule: tournament.rules?.skunkRule ?? "off",
          eventId: tournament.eventId,
          tournamentId: tournament.id,
          bracketMatchId: match.id,
        },
      });
      setLocation(`/score/${created.shareCode}`);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not start match");
      setStartingMatchId(null);
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-5xl mx-auto space-y-6">
      <Link
        href={`/dashboard/events/${eventSlug}`}
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      >
        <ChevronLeft size={12} /> {event?.name ?? "Event"}
      </Link>

      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
            {tournament.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {tournament.format.replace("_", " ")} ·{" "}
            <span className="font-display uppercase tracking-widest">
              {tournament.status}
            </span>
            {tournament.bracketSize && ` · ${tournament.bracketSize} seeds`}
          </p>
        </div>
        {canEdit && tournament.status === "draft" && (
          <Button
            onClick={async () => {
              setError(null);
              try {
                await seed.mutateAsync();
              } catch (e) {
                setError((e as ApiError).message ?? "Could not seed");
              }
            }}
            disabled={seed.isPending}
          >
            {seed.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Seeding…
              </>
            ) : (
              <>
                <Shuffle size={14} /> Seed bracket
              </>
            )}
          </Button>
        )}
      </header>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <BoardOverview
        matches={matches}
        playerNames={playerNames}
      />

      {matches.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Not seeded yet</CardTitle>
            <CardDescription>
              Register players on the event page, then seed the bracket here to
              materialize matches.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex gap-6 min-w-min">
            {byRound.map(([round, list]) => (
              <div
                key={round}
                className="space-y-3 min-w-[16rem]"
              >
                <h2 className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
                  Round {round}
                </h2>
                {list.map((m) => (
                  <MatchCard
                    key={m.id}
                    match={m}
                    playerNames={playerNames}
                    canEdit={canEdit}
                    canStart={
                      canEdit &&
                      !m.gameId &&
                      m.winnerSlot === null &&
                      !!m.slot1.playerId &&
                      !!m.slot2.playerId
                    }
                    onStart={() => startMatch(m)}
                    starting={startingMatchId === m.id}
                    onAssignBoard={(boardNumber) =>
                      setBoard.mutate({ matchId: m.id, boardNumber })
                    }
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({
  match,
  playerNames,
  canEdit,
  canStart,
  onStart,
  starting,
  onAssignBoard,
}: {
  match: BracketMatch;
  playerNames: Map<string, string>;
  canEdit: boolean;
  canStart: boolean;
  onStart: () => void;
  starting: boolean;
  onAssignBoard: (boardNumber: number | null) => void;
}) {
  function slotLabel(slot: BracketMatch["slot1"]): string {
    if (slot.playerId) return playerNames.get(slot.playerId) ?? "Player";
    if (slot.teamId) return playerNames.get(slot.teamId) ?? "Team";
    if (slot.source === "winner_of") return "Winner of prev";
    if (slot.seed) return `Seed ${slot.seed}`;
    return "—";
  }
  const slot1Won = match.winnerSlot === 1;
  const slot2Won = match.winnerSlot === 2;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="divide-y divide-border">
        <div
          className={`flex items-center justify-between gap-2 p-2.5 text-sm ${
            slot1Won ? "bg-primary/10 font-semibold" : ""
          }`}
        >
          <span className="truncate flex-1">{slotLabel(match.slot1)}</span>
          {slot1Won && <Trophy size={12} className="text-primary" />}
        </div>
        <div
          className={`flex items-center justify-between gap-2 p-2.5 text-sm ${
            slot2Won ? "bg-primary/10 font-semibold" : ""
          }`}
        >
          <span className="truncate flex-1">{slotLabel(match.slot2)}</span>
          {slot2Won && <Trophy size={12} className="text-primary" />}
        </div>
      </div>
      <div className="px-2.5 py-1.5 border-t border-border flex items-center justify-between gap-2 bg-muted/30">
        <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
          {match.boardNumber != null && (
            <span className="inline-flex items-center gap-1 text-primary">
              <Layers size={10} /> Board {match.boardNumber}
            </span>
          )}
          <span>{match.gameId ? "Played" : "Pending"}</span>
        </span>
        <div className="flex items-center gap-1">
          {canEdit && !match.gameId && match.winnerSlot === null && (
            <BoardPicker
              current={match.boardNumber}
              onPick={onAssignBoard}
            />
          )}
          {canStart && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onStart}
              disabled={starting}
              className="h-7 px-2"
            >
              {starting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <>
                  <Play size={12} /> Start
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BoardPicker({
  current,
  onPick,
}: {
  current: number | null;
  onPick: (n: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-[10px] font-display uppercase tracking-widest"
        onClick={() => setOpen((o) => !o)}
      >
        <Layers size={10} /> {current ? `B${current}` : "Board"}
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 bg-card border border-border rounded-md shadow-lg p-1 flex gap-1">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              onClick={() => {
                onPick(n);
                setOpen(false);
              }}
              className={`w-7 h-7 text-xs font-display tabular-nums rounded ${
                current === n
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              }`}
            >
              {n}
            </button>
          ))}
          {current != null && (
            <button
              onClick={() => {
                onPick(null);
                setOpen(false);
              }}
              className="w-7 h-7 rounded hover:bg-muted text-muted-foreground inline-flex items-center justify-center"
              aria-label="Clear board"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function BoardOverview({
  matches,
  playerNames,
}: {
  matches: BracketMatch[];
  playerNames: Map<string, string>;
}) {
  const active = matches.filter(
    (m) => m.boardNumber != null && m.winnerSlot === null,
  );
  if (active.length === 0) return null;
  const byBoard = new Map<number, BracketMatch[]>();
  for (const m of active) {
    const list = byBoard.get(m.boardNumber!) ?? [];
    list.push(m);
    byBoard.set(m.boardNumber!, list);
  }
  const ordered = Array.from(byBoard.entries()).sort((a, b) => a[0] - b[0]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers size={16} /> Boards in play
        </CardTitle>
        <CardDescription>
          Live + queued matches grouped by their assigned board.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {ordered.map(([board, list]) => (
            <div
              key={board}
              className="rounded-md border border-border bg-muted/30 p-3 space-y-2"
            >
              <div className="text-[10px] font-display uppercase tracking-widest text-primary">
                Board {board}
              </div>
              {list.map((m) => {
                const label1 = m.slot1.playerId
                  ? (playerNames.get(m.slot1.playerId) ?? "—")
                  : "—";
                const label2 = m.slot2.playerId
                  ? (playerNames.get(m.slot2.playerId) ?? "—")
                  : "—";
                return (
                  <div key={m.id} className="text-sm">
                    <div className="truncate">{label1}</div>
                    <div className="text-xs text-muted-foreground">vs</div>
                    <div className="truncate">{label2}</div>
                    <div className="text-[10px] font-display uppercase tracking-widest text-muted-foreground mt-1">
                      R{m.round}P{m.position} ·{" "}
                      {m.gameId ? "playing" : "queued"}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
